import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { query } from '../api'

import Loader from '../components/Loader'
import Table from '../components/Table'
import { classNames } from '../helper'
import { FUNNEL_FORM_SOURCE } from '../helper/funnelDropouts'
import { getFunnelLeads } from '../helper/funnelLeads'

const AMPEL_STYLES = {
  GRUEN: 'bg-green-50 text-green-700 ring-green-600/20',
  GELB: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
  ROT: 'bg-red-50 text-red-700 ring-red-600/10'
}

const columns = [
  {
    header: 'EINGANG',
    accessorKey: 'createdAt',
    cell: (props) => (
      <span>{format(new Date(props.getValue()), 'dd.MM.yyyy')}</span>
    )
  },
  {
    header: 'E-MAIL-ADRESSE',
    accessorKey: 'email'
  },
  {
    header: 'FIRMA',
    accessorKey: 'company',
    cell: ({ row }) => (
      <span>
        {row.original.company || '–'}
        {row.original.employees && (
          <span className='ml-1 text-gray-400'>({row.original.employees})</span>
        )}
      </span>
    )
  },
  {
    header: 'AMPEL',
    accessorKey: 'lead_ampel',
    cell: ({ row }) =>
      row.original.lead_ampel ? (
        <span
          className={classNames(
            'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset',
            AMPEL_STYLES[row.original.lead_ampel]
          )}
        >
          {row.original.lead_ampel}
          {row.original.lead_score !== null && ` · ${row.original.lead_score}`}
        </span>
      ) : (
        '–'
      )
  },
  {
    header: 'STATUS',
    accessorKey: 'status'
  },
  {
    header: 'LIFECYCLE',
    accessorKey: 'lifecycle_stage',
    cell: (props) => props.getValue() || '–'
  },
  {
    header: 'QUELLE',
    accessorKey: 'source',
    cell: ({ row }) =>
      row.original.created_via_admin ? 'Aivy OS' : row.original.source
  },
  {
    header: 'KUNDE',
    accessorKey: 'partner_id',
    cell: (props) =>
      props.getValue() ? (
        <Link
          to={`/customer/${props.getValue()}`}
          className='text-indigo-600 hover:text-indigo-900'
        >
          Öffnen
        </Link>
      ) : (
        '–'
      )
  },
  {
    header: 'HUBSPOT',
    accessorKey: 'hubspot_url',
    cell: (props) =>
      props.getValue() ? (
        <a
          href={props.getValue()}
          target='_blank'
          rel='noreferrer'
          className='text-indigo-600 hover:text-indigo-900'
        >
          Öffnen
        </a>
      ) : (
        '–'
      )
  }
]

const Leads = () => {
  const { data: forms, isLoading } = useQuery({
    queryKey: ['funnel-forms'],
    queryFn: () =>
      query({
        query: 'listForms',
        variables: {
          filter: { form_source: { eq: FUNNEL_FORM_SOURCE } }
        }
      })
  })

  const leads = useMemo(() => getFunnelLeads(forms || []), [forms])

  if (isLoading) {
    return <Loader loadingText={'Leads werden geladen ...'} />
  }

  return (
    <div>
      <div className='sm:flex sm:items-center'>
        <div className='sm:flex-auto'>
          <h1 className='text-base font-semibold leading-6 text-gray-900'>
            Leads
          </h1>
          <p className='mt-2 text-sm text-gray-700'>
            Abgeschlossene Funnel-Leads, neueste zuerst. Wer den Funnel nicht
            abgeschlossen hat, steht unter{' '}
            <Link
              to='/funnel-dropouts'
              className='text-indigo-600 hover:text-indigo-900'
            >
              Funnel-Abbrecher
            </Link>
            .
          </p>
        </div>
      </div>
      <div className='mt-8 flow-root'>
        <div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
          <div className='inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8'>
            <Table data={leads} columns={columns} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Leads
